import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MarkerType,
  Handle,
  Position,
  BackgroundVariant,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  MiniMap,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import type {
  ApplicableMetricLevel,
  MetricLevelConfig,
  MetricLevelInstance,
} from '../../services/reviewEntityMetricLevelService';

const NODE_WIDTH = 220;
const HORIZONTAL_GAP = NODE_WIDTH + 36;
const VERTICAL_GAP = 130;
const PROJECT_NODE_ID = '__project__';

export interface LevelCatalogEntry {
  id: number;
  name: string;
  display_label: string;
  level_number: number;
  is_period_based?: boolean;
}

interface LevelNodeData {
  label: string;
  subtitle: string;
  dateRange?: string;
  role: 'project' | 'level' | 'instance';
  levelNumber?: number;
  /** Branch color so nodes under different parents are easy to tell apart. */
  palette?: { bg: string; border: string; text: string; sub: string };
}

/** Distinct palettes for branch nodes (any parent instance). Cycles if more branches than entries. */
const BRANCH_PALETTES = [
  { bg: '#e8f5e9', border: '#66bb6a', text: '#1b5e20', sub: '#2e7d32' }, // green
  { bg: '#fff3e0', border: '#ffa726', text: '#e65100', sub: '#ef6c00' }, // orange
  { bg: '#e3f2fd', border: '#42a5f5', text: '#0d47a1', sub: '#1565c0' }, // blue
  { bg: '#fce4ec', border: '#ec407a', text: '#880e4f', sub: '#c2185b' }, // pink
  { bg: '#f3e5f5', border: '#ab47bc', text: '#4a148c', sub: '#7b1fa2' }, // purple
  { bg: '#e0f7fa', border: '#26c6da', text: '#006064', sub: '#00838f' }, // cyan
  { bg: '#fbe9e7', border: '#ff7043', text: '#bf360c', sub: '#d84315' }, // deep orange
  { bg: '#efebe9', border: '#8d6e63', text: '#3e2723', sub: '#5d4037' }, // brown
  { bg: '#f9fbe7', border: '#c0ca33', text: '#827717', sub: '#9e9d24' }, // lime
  { bg: '#ede7f6', border: '#7e57c2', text: '#311b92', sub: '#512da8' }, // deep purple
  { bg: '#e8eaf6', border: '#5c6bc0', text: '#1a237e', sub: '#303f9f' }, // indigo
  { bg: '#e0f2f1', border: '#26a69a', text: '#004d40', sub: '#00695c' }, // teal
];

const PROJECT_PALETTE = {
  bg: '#1565c0',
  border: '#0d47a1',
  text: '#fff',
  sub: 'rgba(255,255,255,0.85)',
};

const LEVEL_ORDER_PALETTE = {
  bg: '#e3f2fd',
  border: '#90caf9',
  text: '#0d47a1',
  sub: '#1565c0',
};

const DEFAULT_INSTANCE_PALETTE = BRANCH_PALETTES[4];

const MetricHierarchyNode: React.FC<{ data: LevelNodeData }> = ({ data }) => {
  const palette =
    data.palette ||
    (data.role === 'project'
      ? PROJECT_PALETTE
      : data.role === 'level'
      ? LEVEL_ORDER_PALETTE
      : DEFAULT_INSTANCE_PALETTE);

  return (
    <div
      style={{
        width: NODE_WIDTH,
        minHeight: data.dateRange ? 78 : 64,
        borderRadius: 8,
        border: `2px solid ${palette.border}`,
        background: palette.bg,
        padding: '8px 12px',
        textAlign: 'center',
        boxShadow:
          data.role === 'project'
            ? '0 4px 12px rgba(21,101,192,0.35)'
            : '0 1px 4px rgba(0,0,0,0.1)',
        position: 'relative',
        cursor: 'grab',
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: palette.border, width: 8, height: 8, top: -5 }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: palette.border, width: 8, height: 8, bottom: -5 }}
      />
      <div
        style={{
          fontWeight: 600,
          fontSize: 13,
          color: palette.text,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={data.label}
      >
        {data.label}
      </div>
      <div
        style={{
          fontSize: 11,
          color: palette.sub,
          marginTop: 2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={data.subtitle}
      >
        {data.subtitle}
      </div>
      {data.dateRange ? (
        <div
          style={{
            fontSize: 10,
            color: palette.sub,
            marginTop: 2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={data.dateRange}
        >
          {data.dateRange}
        </div>
      ) : null}
    </div>
  );
};

const nodeTypes = { metricHierarchyNode: MetricHierarchyNode };

function compareInstancesChronologically(
  a: MetricLevelInstance | undefined,
  b: MetricLevelInstance | undefined
): number {
  const as = a?.start_date || '';
  const bs = b?.start_date || '';
  if (as !== bs) return as.localeCompare(bs);
  return (a?.name || '').localeCompare(b?.name || '');
}

/**
 * Place each subtree so children sit directly under their parent (no cross-branch
 * interleaving). Returns center-x of the laid-out subtree.
 */
function layoutSubtree(
  nodeId: string,
  depth: number,
  childrenOf: Map<string, string[]>,
  posMap: Map<string, { x: number; y: number }>,
  cursor: { x: number }
): number {
  const children = childrenOf.get(nodeId) || [];
  if (children.length === 0) {
    const x = cursor.x;
    posMap.set(nodeId, { x, y: depth * VERTICAL_GAP });
    cursor.x += HORIZONTAL_GAP;
    return x;
  }

  const childCenters = children.map((childId) =>
    layoutSubtree(childId, depth + 1, childrenOf, posMap, cursor)
  );
  const centerX = (childCenters[0] + childCenters[childCenters.length - 1]) / 2;
  posMap.set(nodeId, { x: centerX, y: depth * VERTICAL_GAP });
  return centerX;
}

type BranchPalette = (typeof BRANCH_PALETTES)[number];

/**
 * Assign a distinct palette to every branch node (direct PROJECT children + any
 * instance that has children). Leaves inherit from the nearest colored ancestor
 * (so sprints share their release color; releases differ from each other).
 * Palettes cycle if there are more branch nodes than BRANCH_PALETTES.
 */
function buildBranchPalettes(
  childrenOf: Map<string, string[]>
): Map<string, BranchPalette> {
  const paletteById = new Map<string, BranchPalette>();
  let colorIdx = 0;

  const walk = (nodeId: string) => {
    const kids = childrenOf.get(nodeId) || [];
    kids.forEach((childId) => {
      const grandkids = childrenOf.get(childId) || [];
      const isBranchNode = grandkids.length > 0 || nodeId === PROJECT_NODE_ID;
      if (isBranchNode) {
        paletteById.set(
          childId,
          BRANCH_PALETTES[colorIdx % BRANCH_PALETTES.length]
        );
        colorIdx += 1;
      }
      walk(childId);
    });
  };

  walk(PROJECT_NODE_ID);
  return paletteById;
}

function paletteForInstance(
  instanceId: string,
  byId: Map<string, MetricLevelInstance>,
  paletteById: Map<string, BranchPalette>
): BranchPalette {
  let currentId: string | undefined = instanceId;
  while (currentId) {
    const assigned = paletteById.get(currentId);
    if (assigned) return assigned;
    const current: MetricLevelInstance | undefined = byId.get(currentId);
    if (!current) break;
    const parentId = current.parent_instance_id;
    currentId =
      parentId && byId.has(parentId) ? parentId : undefined;
  }
  return DEFAULT_INSTANCE_PALETTE;
}

/** Level-order ladder: PROJECT → coarsest → … → finest (by level_number desc). */
function buildLevelOrderGraph(
  levels: ApplicableMetricLevel[],
  entityName: string
): { nodes: Node<LevelNodeData>[]; edges: Edge[] } {
  const ordered = [...levels].sort((a, b) => b.level_number - a.level_number);
  const posMap = new Map<string, { x: number; y: number }>();
  posMap.set(PROJECT_NODE_ID, { x: 0, y: 0 });
  ordered.forEach((_, idx) => {
    posMap.set(`level-${ordered[idx].id}`, { x: 0, y: (idx + 1) * VERTICAL_GAP });
  });

  const nodes: Node<LevelNodeData>[] = [
    {
      id: PROJECT_NODE_ID,
      type: 'metricHierarchyNode',
      position: posMap.get(PROJECT_NODE_ID) || { x: 0, y: 0 },
      data: {
        label: entityName || 'Project',
        subtitle: 'PROJECT (implicit top)',
        role: 'project',
        palette: PROJECT_PALETTE,
      },
    },
  ];

  ordered.forEach((level) => {
    nodes.push({
      id: `level-${level.id}`,
      type: 'metricHierarchyNode',
      position: posMap.get(`level-${level.id}`) || { x: 0, y: 0 },
      data: {
        label: level.display_label,
        subtitle: `L${level.level_number} · ${level.name}${
          level.is_period_based ? ' · period' : ''
        }`,
        role: 'level',
        levelNumber: level.level_number,
        palette: LEVEL_ORDER_PALETTE,
      },
    });
  });

  const edges: Edge[] = [];
  let parentId = PROJECT_NODE_ID;
  ordered.forEach((level) => {
    const childId = `level-${level.id}`;
    edges.push({
      id: `e-${parentId}-${childId}`,
      source: parentId,
      target: childId,
      type: 'smoothstep',
      markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#90a4ae' },
      style: { stroke: '#90a4ae', strokeWidth: 1.5 },
    });
    parentId = childId;
  });

  return { nodes, edges };
}

/** Instance parent tree under PROJECT — children grouped under parents, colored by branch. */
function buildInstanceGraph(
  config: MetricLevelConfig,
  entityName: string,
  levelCatalog: LevelCatalogEntry[] = []
): { nodes: Node<LevelNodeData>[]; edges: Edge[] } {
  const instances = config.instances || [];
  const byId = new Map(instances.map((i) => [i.id, i]));

  const catalogById = new Map<number, LevelCatalogEntry>();
  [...(config.applicable_metric_levels || []), ...levelCatalog].forEach((entry) => {
    if (entry?.id != null) {
      catalogById.set(Number(entry.id), entry);
    }
  });

  const resolveLevelMeta = (inst: MetricLevelInstance) => {
    const levelId = Number(inst.metric_level_id);
    const fromCatalog = catalogById.get(levelId);
    const displayLabel =
      inst.metric_level_display_label ||
      fromCatalog?.display_label ||
      inst.metric_level_name ||
      fromCatalog?.name ||
      null;
    const levelNumber =
      fromCatalog?.level_number ??
      (config.applicable_metric_levels || []).find((l) => Number(l.id) === levelId)?.level_number;
    return { displayLabel, levelNumber };
  };

  // parent → children (PROJECT for unparented / orphaned parents)
  const childrenOf = new Map<string, string[]>();
  childrenOf.set(PROJECT_NODE_ID, []);
  instances.forEach((inst) => {
    const parentId =
      inst.parent_instance_id && byId.has(inst.parent_instance_id)
        ? inst.parent_instance_id
        : PROJECT_NODE_ID;
    if (!childrenOf.has(parentId)) childrenOf.set(parentId, []);
    childrenOf.get(parentId)!.push(inst.id);
  });

  // Chronological order within each sibling group
  childrenOf.forEach((childIds, parentId) => {
    childIds.sort((a, b) =>
      compareInstancesChronologically(byId.get(a), byId.get(b))
    );
    childrenOf.set(parentId, childIds);
  });

  // Branch colors: L3 milestones AND L2 releases (any parent) get unique colors;
  // leaf sprints inherit from their release. Cycles if > BRANCH_PALETTES.length.
  const paletteById = buildBranchPalettes(childrenOf);
  const resolvePalette = (instanceId: string) =>
    paletteForInstance(instanceId, byId, paletteById);

  const posMap = new Map<string, { x: number; y: number }>();
  layoutSubtree(PROJECT_NODE_ID, 0, childrenOf, posMap, { x: 0 });

  // Center the whole tree around x=0
  const xs = Array.from(posMap.values()).map((p) => p.x);
  const mid = xs.length ? (Math.min(...xs) + Math.max(...xs)) / 2 : 0;
  posMap.forEach((pos, id) => {
    posMap.set(id, { x: pos.x - mid, y: pos.y });
  });

  const nodes: Node<LevelNodeData>[] = [
    {
      id: PROJECT_NODE_ID,
      type: 'metricHierarchyNode',
      position: posMap.get(PROJECT_NODE_ID) || { x: 0, y: 0 },
      data: {
        label: entityName || 'Project',
        subtitle: 'PROJECT (implicit top)',
        role: 'project',
        palette: PROJECT_PALETTE,
      },
    },
  ];

  instances.forEach((inst) => {
    const { displayLabel, levelNumber } = resolveLevelMeta(inst);
    const levelLabel = displayLabel || 'Level';
    const levelPrefix = levelNumber != null ? `L${levelNumber} · ` : '';
    let dateRange: string | undefined;
    if (inst.start_date || inst.end_date) {
      dateRange = `${inst.start_date || '—'} → ${inst.end_date || '—'}`;
    }
    const palette = resolvePalette(inst.id);
    nodes.push({
      id: inst.id,
      type: 'metricHierarchyNode',
      position: posMap.get(inst.id) || { x: 0, y: 0 },
      data: {
        label: inst.name,
        subtitle: `${levelPrefix}${levelLabel}`,
        dateRange,
        role: 'instance',
        levelNumber,
        palette,
      },
    });
  });

  const edges: Edge[] = [];
  instances.forEach((inst) => {
    const parentId =
      inst.parent_instance_id && byId.has(inst.parent_instance_id)
        ? inst.parent_instance_id
        : PROJECT_NODE_ID;
    const palette = resolvePalette(inst.id);
    edges.push({
      id: `e-${parentId}-${inst.id}`,
      source: parentId,
      target: inst.id,
      type: 'smoothstep',
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 16,
        height: 16,
        color: palette.border,
      },
      style: { stroke: palette.border, strokeWidth: 1.5 },
    });
  });

  return { nodes, edges };
}

function minimapNodeColor(node: Node<LevelNodeData>): string {
  if (node.data?.palette?.border) return node.data.palette.border;
  if (node.data?.role === 'project') return PROJECT_PALETTE.border;
  if (node.data?.role === 'level') return LEVEL_ORDER_PALETTE.border;
  return DEFAULT_INSTANCE_PALETTE.border;
}

/** Interactive canvas for the expanded dialog — nodes are draggable. */
const InteractiveFlow: React.FC<{
  initialNodes: Node<LevelNodeData>[];
  initialEdges: Edge[];
}> = ({ initialNodes, initialEdges }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(
    initialNodes.map((n) => ({ ...n, draggable: true }))
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const { fitView } = useReactFlow();

  useEffect(() => {
    setNodes(initialNodes.map((n) => ({ ...n, draggable: true })));
    setEdges(initialEdges);
    const timer = window.setTimeout(() => {
      fitView({ padding: 0.15, duration: 200 });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [initialNodes, initialEdges, setNodes, setEdges, fitView]);

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable
        panOnDrag
        zoomOnScroll
        minZoom={0.2}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
      >
        <Controls />
        <MiniMap
          nodeColor={minimapNodeColor}
          maskColor="rgba(0,0,0,0.08)"
          style={{ border: '1px solid #e0e0e0' }}
        />
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#e0e0e0" />
      </ReactFlow>
    </Box>
  );
};

export interface MetricLevelHierarchyGraphProps {
  config: MetricLevelConfig;
  entityName: string;
  height?: number;
  /** Fallback catalog (e.g. entity-type levels) when instance enrichment lacks labels. */
  levelCatalog?: LevelCatalogEntry[];
}

const MetricLevelHierarchyGraph: React.FC<MetricLevelHierarchyGraphProps> = ({
  config,
  entityName,
  height = 320,
  levelCatalog = [],
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const hasInstances = (config.instances || []).length > 0;
  const hasLevels =
    (config.applicable_metric_levels || []).length > 0 || levelCatalog.length > 0;

  const { nodes, edges } = useMemo(() => {
    if (hasInstances) return buildInstanceGraph(config, entityName, levelCatalog);
    const levelsForOrder =
      (config.applicable_metric_levels || []).length > 0
        ? config.applicable_metric_levels
        : (levelCatalog as ApplicableMetricLevel[]);
    if (levelsForOrder.length > 0) return buildLevelOrderGraph(levelsForOrder, entityName);
    return { nodes: [] as Node<LevelNodeData>[], edges: [] as Edge[] };
  }, [config, entityName, hasInstances, levelCatalog]);

  const openDialog = useCallback(() => setDialogOpen(true), []);
  const closeDialog = useCallback(() => setDialogOpen(false), []);

  if (!hasLevels && !hasInstances) {
    return (
      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
        <Typography variant="body2" color="text.secondary">
          Select and save applicable levels to see the hierarchy (PROJECT at top, then ordered
          levels).
        </Typography>
      </Paper>
    );
  }

  const title = hasInstances ? 'Instance hierarchy' : 'Level order';
  const subtitle = hasInstances
    ? 'Top = PROJECT. Children sit under their parent; each milestone and release branch uses its own color (sprints inherit the release).'
    : 'Top = PROJECT. Levels cascade by roll-up order (coarsest → finest). Add instances to see the live tree.';

  return (
    <>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: 'grey.100',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">{title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
          <Tooltip title="Open larger hierarchy view">
            <IconButton size="small" onClick={openDialog} aria-label="expand hierarchy">
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ height, width: '100%' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            panOnDrag
            zoomOnScroll
            minZoom={0.3}
            maxZoom={1.5}
            proOptions={{ hideAttribution: true }}
          >
            <Controls showInteractive={false} />
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#e0e0e0" />
          </ReactFlow>
        </Box>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h6" component="span">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Drag nodes to rearrange if they overlap. Pan and zoom to explore the full tree.
            </Typography>
          </Box>
          <IconButton onClick={closeDialog} aria-label="close hierarchy dialog">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {dialogOpen && (
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
              <ReactFlowProvider>
                <InteractiveFlow initialNodes={nodes} initialEdges={edges} />
              </ReactFlowProvider>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MetricLevelHierarchyGraph;
